import exceptionIcon from '@jetbrains/icons/exception.svg'
import ErrorMessage from '@jetbrains/ring-ui/components/error-message/error-message'
import React from 'react'
import type {EntryPointProps} from 'react-relay'
import {usePreloadedQuery} from 'react-relay'

import {getPipelinesHref} from '../../../../routes'
import Loader from '../../../common/Loader/Loader'
import type {BuildOverviewTabEntryPoint} from '../../BuildPage/BuildOverviewTab/BuildOverviewTab.entryPoint'
import type {BuildTestsTabEntryPoint} from '../../BuildPage/BuildTestsTab/BuildTestsTab.entryPoint'
import FullScreenErrorWrapper from '../components/common/FullScreenErrorWrapper/FullScreenErrorWrapper'
import Link from '../components/common/Link/Link'
import {serializeError} from '../utils/error'

import type {PipelineRunPagePipelineQuery} from './__generated__/PipelineRunPagePipelineQuery.graphql'
import PipelineRunChart from './PipelineRunChart/PipelineRunChart'
import PipelineRunHeader from './PipelineRunHeader/PipelineRunHeader'
import {usePipelineRunPage} from './PipelineRunPage.hooks'
import {pipelineQuery} from './PipelineRunPage.queries'
import SelectedJob from './SelectedJob/SelectedJob'

import styles from './PipelineRunPage.css'

type Queries = {pipeline: PipelineRunPagePipelineQuery}
export type NestedEntryPoints = {
  overviewTab?: typeof BuildOverviewTabEntryPoint
  testsTab?: typeof BuildTestsTabEntryPoint
}
function PipelineRunPage({
  queries,
  entryPoints,
}: EntryPointProps<Queries, NestedEntryPoints, {}, {}>) {
  const {project} = usePreloadedQuery(pipelineQuery, queries.pipeline)
  const {jobId, buildError, pipelineError, buildLoading, pipelineLoading} = usePipelineRunPage()

  if (buildLoading || pipelineLoading) {
    return <Loader />
  }

  const error = buildError ?? pipelineError
  if (error) {
    return (
      <FullScreenErrorWrapper>
        <ErrorMessage
          icon={exceptionIcon}
          message="Error while loading pipeline"
          description={serializeError(error)}
        >
          <Link to={getPipelinesHref()}>{'Go to the home page'}</Link>
        </ErrorMessage>
      </FullScreenErrorWrapper>
    )
  }

  return (
    <>
      <PipelineRunHeader pipelineKey={project} />
      <div className={styles.chartWrapper}>
        <PipelineRunChart />
      </div>
      {jobId != null && (
        <SelectedJob
          key={jobId}
          jobId={Number(jobId)}
          overviewTabEntryPoint={entryPoints.overviewTab}
          testsTabEntryPoint={entryPoints.testsTab}
        />
      )}
    </>
  )
}

export default PipelineRunPage
export type PipelineRunPageComponent = typeof PipelineRunPage
